/**
 * Tag presentation ↔ canonical wire mapping for the Contact Lists page
 * (subtask 04; entity renamed Imports → Contact Lists, Trello #711 cl.1).
 *
 * The wire values (`raw` | `bct` | `ai` | `enriched` | `exported` | `combined`)
 * are the canonical `IMPORT_TAG_DEFS` keys from the epic's *Canonical contracts
 * & vocabularies* — the FE NEVER sends the superseded DDL-comment labels
 * (`raw_export` / `AI-reviewed`). This module is the single place the FE
 * translates between the human chip/pill LABEL and the canonical wire VALUE.
 *
 * Colours are mapped to `@nu-assist/ui` DESIGN-SYSTEM TOKENS (no inline hex,
 * `feedback_no_inline_styles`). The prototype's raw hex → token decision (incl.
 * the new `info` violet + `conflict` orange tokens added to the design system
 * this subtask) is documented in the PRD's Design-token mapping table.
 */

import type { ImportFormat, ImportTag } from "@nu-assist/contracts";

/** A tag's human label + the design-system pill colour classes. */
export interface TagDef {
  /** Canonical wire value (the stored `IMPORT_TAG_DEFS` key). */
  value: ImportTag;
  /** Human-readable label rendered on the chip + the pill. */
  label: string;
  /** Tailwind classes (design-system tokens) for the pill surface. */
  pillClass: string;
  /**
   * How the DETAIL page dresses itself for this type (Trello #711 cl.4) —
   * the "per-type color header" that tells a recruiter which kind of list
   * they are looking at before they read a word. Kept on the same def as
   * `pillClass` so the badge, the header stripe and the info cards can never
   * disagree the way the list pill and the detail pill once did.
   */
  theme: TagTheme;
}

/**
 * The per-type dressing for the Contact List detail page.
 *
 * Every value written into one of these fields must be a token that ALREADY BAKES ITS ALPHA IN
 * (`--accent-muted`, `--warning-strong`, `--border-hover`, …) rather than a
 * `/<alpha>` modifier on a base colour. That is not stylistic: `tokens.css`
 * records twice that "Tailwind's `/<alpha>` modifier silently no-ops on a hex
 * CSS-var color in v3" — `hover:bg-warning/90` and `hover:bg-error/20` were
 * both shipped as no-ops before anyone noticed. A tint that silently does
 * nothing is exactly the failure this table exists to prevent, because the
 * whole point is that the page LOOKS different per type.
 */
export interface TagTheme {
  /** Left-edge stripe beside the page title. */
  accentBorder: string;
  /** Border on the four info cards (Date / Contact list title / …). */
  cardBorder: string;
  /** Colour of the small card labels above each value. */
  cardLabel: string;
  /** Card surface tint. Neutral types keep the plain raised surface. */
  cardSurface: string;
}

/**
 * Ordered tag definitions — drives BOTH the filter chips (R-F1, via the
 * `TYPE_TAGS` subset) and the per-row Type pill (R-F6). One source of truth so
 * a chip and its pill always agree.
 *
 * Token mapping (prototype hex → token):
 *   raw      grey   #B0B0B0 → text-secondary on a muted neutral surface
 *   bct      amber  #F59E0B → warning tokens
 *   ai       accent var(--accent) → accent tokens
 *   enriched accent (success-adjacent) → success tokens (enrichment = added value)
 *   exported slate  #94A3B8 → text-secondary neutral
 *   combined violet #A78BFA → the new `info` violet token
 */
/**
 * NOTE — only the THREE type tags (`raw` / `bct` / `ai`) can ever reach a
 * detail page: `deriveTypeTag` returns nothing else, and `typeDef` is the only
 * consumer. The `enriched` / `exported` / `combined` themes below therefore
 * never render. They exist to satisfy this required field on every `TagDef`,
 * not because they are used — which is exactly how the `border-success/40`
 * no-op in the first version of this table survived review unseen. Anything
 * you write in those three is unvalidated by construction; if a future feature
 * dresses a page by a non-type tag, verify it in a browser before trusting it.
 */
export const TAG_DEFS: readonly TagDef[] = [
  {
    value: "raw",
    label: "Raw Import",
    pillClass: "bg-white/5 text-text-secondary border border-border",
    theme: {
      accentBorder: "border-border",
      cardBorder: "border-border",
      cardLabel: "text-text-muted",
      cardSurface: "bg-surface",
    },
  },
  {
    value: "bct",
    label: "Company Map",
    pillClass: "bg-warning-muted text-warning-ink border border-warning-strong",
    theme: {
      accentBorder: "border-warning-strong",
      cardBorder: "border-warning-strong",
      cardLabel: "text-warning-ink",
      cardSurface: "bg-warning-muted",
    },
  },
  {
    value: "ai",
    label: "AI Review",
    pillClass: "bg-accent-muted text-accent-ink border border-border-hover",
    theme: {
      accentBorder: "border-accent",
      cardBorder: "border-border-hover",
      cardLabel: "text-accent-ink",
      cardSurface: "bg-accent-muted",
    },
  },
  {
    value: "enriched",
    label: "Enriched",
    // `bg-success/15` + `border-success/25` were silent no-ops: `--success` is
    // a hex CSS var, and tokens.css records twice that Tailwind v3's `/<alpha>`
    // modifier emits nothing for those. `--success-muted` is the token that
    // exists precisely so this pill can have a 15% fill (see its comment in
    // tokens.css, which names this pill family as the reason it was added).
    pillClass: "bg-success-muted text-success-ink border border-success",
    theme: {
      accentBorder: "border-success",
      cardBorder: "border-success",
      cardLabel: "text-success-ink",
      cardSurface: "bg-success-muted",
    },
  },
  {
    value: "exported",
    label: "Exported",
    pillClass: "bg-white/5 text-text-secondary border border-border",
    theme: {
      accentBorder: "border-border",
      cardBorder: "border-border",
      cardLabel: "text-text-muted",
      cardSurface: "bg-surface",
    },
  },
  {
    value: "combined",
    label: "Combined",
    // Contrast, verified against `tokens.css` (/pr-review iter 1, Agent C M5):
    // `combined` IS rendered (Type column, detail badge, picker). `text-info`
    // on `bg-info-muted` is 5.39:1 in light (#6a3fd0 on #ede7fd) and 5.70:1 in
    // dark (#a78bfa on its 12% tint over --surface), both AA for text. The
    // old "~2.7:1" figure predates Tide 2, which deepened light `--info`; that
    // is why `info` deliberately has no `-ink` token (see `tagDefs.test.ts`).
    pillClass: "bg-info-muted text-info border border-info-strong",
    theme: {
      accentBorder: "border-info-strong",
      cardBorder: "border-info-strong",
      cardLabel: "text-info",
      cardSurface: "bg-info-muted",
    },
  },
] as const;

/**
 * The dressing an untyped list gets — a Raw Import, and anything whose type
 * cannot be derived. Exported from HERE, the file that owns every other theme,
 * rather than read out of `TAG_DEF_BY_VALUE.raw.theme` at the call site: that
 * indirection made the neutral default depend on `raw` staying a member of
 * `ImportTag` AND on its theme staying neutral, neither of which this file
 * promises (/pr-review iter 1, Agent A 2(d)). Kept identical to `raw`'s theme
 * by construction, so the two cannot drift.
 */
export const NEUTRAL_TAG_THEME: TagTheme = {
  accentBorder: "border-border",
  cardBorder: "border-border",
  cardLabel: "text-text-muted",
  cardSurface: "bg-surface",
};

/** Wire value → tag def (for rendering pills from `tags[]`). */
export const TAG_DEF_BY_VALUE: Record<ImportTag, TagDef> = Object.fromEntries(
  TAG_DEFS.map((d) => [d.value, d])
) as Record<ImportTag, TagDef>;

/**
 * The TYPE-class tags — the ones that answer "what kind of contact list is
 * this row?" (Trello #711 cl.1). Exactly one is shown per row (the Type
 * column) and only these are offered as filter chips. `enriched` / `exported`
 * / `combined` remain valid wire values (`?tag=` plumbing untouched) — the UI
 * just stops surfacing them as a type or a filter.
 */
export const TYPE_TAGS: readonly ImportTag[] = ["raw", "bct", "ai", "combined"] as const;

/** Type precedence when deriving from `tags[]`: a derived row can carry
 *  several tags (e.g. `["bct","exported"]`) but has ONE type. */
const TYPE_PRECEDENCE: readonly ImportTag[] = ["ai", "bct", "combined", "raw"] as const;

/** `import_format` → type tag. The format is the immutable provenance
 *  discriminator (same key the server's no-chaining 409 gates on), which makes
 *  it the right FALLBACK when the tags say nothing — but only that. See
 *  `deriveTypeTag` for why the tags are read first. */
const FORMAT_TO_TYPE: Record<ImportFormat, ImportTag> = {
  baby_chops_v1: "raw",
  bct: "bct",
  ai_review: "ai",
  // A combined people list carries tags `['raw','combined']`; `combined` is a
  // type class ahead of `raw` in TYPE_PRECEDENCE so it is labelled "Combined",
  // not "Raw Import" (F7).
  combined: "combined",
  // A list pulled from a Thrive search (epic 2026-10-15-thrive-search-import,
  // 02) is a raw People List: tags `['raw']`, so the "Raw Import" filter chip
  // finds it. Its pill names the producer (`FORMAT_PRODUCER`).
  thrive_search_v1: "raw",
};

/** Separator between a type label and its producer (U+00B7 middle dot), the
 *  same one `TagPills` puts between a derived file's type and its maker. */
export const PRODUCER_SEPARATOR = " · ";

/**
 * The producer a FORMAT names on its type pill, when the format itself is
 * the producer (04): a list pulled from a Thrive search reads "Raw Import ·
 * Thrive". A person producer (`TagPills`' `producedBy`) wins over it — a
 * derived file names who made it; this only speaks for a format that is a
 * channel, never for a derivation.
 */
const FORMAT_PRODUCER: Partial<Record<ImportFormat, string>> = {
  thrive_search_v1: "Thrive",
};

/** The type pill's text for a row: the tag's label, plus the format's
 *  producer when it has one and the tag is that format's own type (a Thrive
 *  pull's later AI Review file is an "AI Review", nothing more). */
export function typeLabel(tag: ImportTag, importFormat?: ImportFormat | null): string {
  const label = TAG_DEF_BY_VALUE[tag].label;
  const producer = importFormat ? FORMAT_PRODUCER[importFormat] : undefined;
  return producer && importFormat && FORMAT_TO_TYPE[importFormat] === tag
    ? `${label}${PRODUCER_SEPARATOR}${producer}`
    : label;
}

/**
 * The row's single type tag: the highest-precedence type-class tag in `tags[]`
 * (ai > bct > combined > raw), falling back to `import_format` when the tags say nothing.
 * `null` when neither does (renders the em-dash placeholder).
 *
 * **Tags win over the format, and the legacy in-place review is why** (F10/D6):
 * a pre-epic review scored the import it was pointed at, so the row is
 * `import_format: "baby_chops_v1"` carrying `["raw","ai"]`. The format calls it
 * an upload; the recruiter looking at the column wants to know it holds AI
 * scores. Reading the format first would relabel every one of those rows "Raw
 * Import" and hide the review — the exact information the old two-pill column
 * was showing. The format only speaks when no type tag is present, which is
 * where it is the honest answer rather than a stale one.
 */
export function deriveTypeTag(
  tags: readonly ImportTag[] | null | undefined,
  importFormat?: ImportFormat | null,
): ImportTag | null {
  const fromTags = tags?.length ? TYPE_PRECEDENCE.find((t) => tags.includes(t)) : undefined;
  if (fromTags) return fromTags;
  if (importFormat && importFormat in FORMAT_TO_TYPE) return FORMAT_TO_TYPE[importFormat];
  return null;
}

/** A filter chip — the prototype's chip row. `All` clears the `tag` filter. */
export interface FilterChip {
  label: string;
  /** Canonical wire value, or `null` for "All" (omit `tag`). */
  value: ImportTag | null;
}

/**
 * Every canonical tag as a chip, `All` first. NOT the Contact Lists page's
 * chip row (that is `FILTER_CHIPS`, type-only since Trello #711 cl.1) — this
 * is the superset other surfaces filter down themselves, e.g. the import
 * picker's finder row, which offers `exported` precisely because "already
 * sent to Thrive" is the thing a finder wants to narrow on.
 */
export const ALL_TAG_CHIPS: readonly FilterChip[] = [
  { label: "All", value: null },
  ...TAG_DEFS.map((d) => ({ label: d.label, value: d.value })),
] as const;

/**
 * The Contact Lists page's chip row: exactly
 * `All | Raw Import | Company Map | AI Review` (Trello #711 cl.1). The
 * lifecycle tags stay valid wire values — the `?tag=` plumbing is untouched —
 * the page just stops offering them as filters, because they answer "what has
 * happened TO this list", not "what kind of list is this".
 */
export const FILTER_CHIPS: readonly FilterChip[] = [
  { label: "All", value: null },
  ...TAG_DEFS.filter((d) => TYPE_TAGS.includes(d.value)).map((d) => ({
    label: d.label,
    value: d.value,
  })),
] as const;
